import { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react';

const COLORS = ['#1A1A1A', '#E4393C', '#0054A6', '#2E8B57'];
const PEN_WIDTH = 4;
const ERASER_WIDTH = 20;

const DrawingCanvas = forwardRef(function DrawingCanvas({ disabled = false }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [color, setColor] = useState('#1A1A1A');
  const [eraser, setEraser] = useState(false);

  useImperativeHandle(ref, () => ({
    getDataUrl: () => canvasRef.current?.toDataURL('image/png') ?? '',
    clear: () => {
      const c = canvasRef.current;
      if (!c) return;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
    },
  }));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFEF7';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = useCallback((e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDraw = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    const pos = getPos(e, canvasRef.current);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, (eraser ? ERASER_WIDTH : PEN_WIDTH) / 2, 0, Math.PI * 2);
    ctx.fillStyle = eraser ? '#FFFEF7' : color;
    ctx.fill();
  }, [disabled, color, eraser, getPos]);

  const moveDraw = useCallback((e) => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.lineWidth = eraser ? ERASER_WIDTH : PEN_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = eraser ? '#FFFEF7' : color;
    ctx.stroke();
    lastPos.current = pos;
  }, [disabled, color, eraser, getPos]);

  const endDraw = useCallback(() => { drawing.current = false; }, []);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative tv-panel overflow-hidden" style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}>
        <canvas
          ref={canvasRef}
          width={600}
          height={400}
          className="w-full h-auto block"
          style={{ cursor: eraser ? 'cell' : 'crosshair', touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
          onContextMenu={(e) => e.preventDefault()}
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={endDraw}
        />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {COLORS.map((c) => (
          <button
            key={c}
            onClick={() => { setColor(c); setEraser(false); }}
            className="w-9 h-9 rounded-full border-4"
            style={{
              backgroundColor: c,
              borderColor: !eraser && color === c ? '#F5C518' : '#1A1A1A',
              boxShadow: !eraser && color === c ? '0 0 0 2px #1A1A1A' : 'none',
            }}
          />
        ))}
        <button
          onClick={() => setEraser((e) => !e)}
          className={`tv-btn text-sm px-3 py-1 ${eraser ? 'tv-btn-yellow' : 'tv-btn-white'}`}
        >
          消しゴム
        </button>
        <button
          onClick={() => {
            const c = canvasRef.current;
            const ctx = c.getContext('2d');
            ctx.fillStyle = '#FFFEF7';
            ctx.fillRect(0, 0, c.width, c.height);
          }}
          className="tv-btn tv-btn-white text-sm px-3 py-1"
        >
          クリア
        </button>
        <button
          onClick={() => {
            const c = canvasRef.current;
            if (!c) return;
            const a = document.createElement('a');
            a.href = c.toDataURL('image/png');
            a.download = 'drawing.png';
            a.click();
          }}
          className="tv-btn tv-btn-white text-sm px-3 py-1"
        >
          💾 保存
        </button>
      </div>
    </div>
  );
});

export default DrawingCanvas;
